// @flow
import React, { Component } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import _ from 'lodash';

import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import Proposition from 'common/modules/proposition/domain/proposition';
import { getUser } from 'statsradio/modules/authentication/session/reducers/sessionReducer';
import SalesPropositionApi from 'statsradio/modules/sales/services/SalesPropositionApi';
import ActionOnPropositionConfirmationDialog from 'statsradio/modules/proposition/components/ActionOnPropositionConfirmationDialog';

import PropositionListing from 'common/modules/proposition/domain/propositionListing';
import SavePropositionDialog from '../../sales/components/proposition/SavePropositionDialog';

export type ActionType =
| 'SEND'
| 'CANCEL'
| 'ACCEPT'
| 'DECLINE'
| 'MODIFY'

export type Action = {
  label: string,
  type: ActionType,
  onAction: () => Promise<void> | void,
}

type Props = any & {
  proposition: PropositionListing,
  onChange: (proposition: Proposition) => void,

  radio: Object,
  token: string,
  actions: {
    openSnackbar: (type: string, message: string) => any,
  },
  t: any,
};

type State = {
  propositionToModify?: Proposition;
  sending: boolean,

  confirmation: {
    action: string,
    open: boolean,
    onConfirm: () => void,
    onCancel: () => void,
    type?: ActionType,
  },
};

function withPropositionActions(WrappedComponent) {
  class WithPropositionActions extends Component<Props, State> {
    state: State = {
      propositionToModify: undefined,
      sending: false,
      confirmation: {
        action: '',
        open: false,
        onConfirm: () => {},
        onCancel: () => {},
      },
    };

    closeConfirmation() {
      this.setState({ confirmation: { ...this.state.confirmation, open: false } });
    }

    clearEditingState = () => this.setState({ propositionToModify: undefined, sending: false });

    waitConfirmation(action, type: ActionType) {
      return new Promise((resolve, reject) => {
        this.setState({
          confirmation: {
            action,
            open: true,
            onConfirm: () => {
              this.closeConfirmation();
              resolve();
            },
            onCancel: () => {
              this.closeConfirmation();
              reject();
            },
            type,
          },
        });
      });
    }

    async onCancelProposition() {
      const { actions, proposition: { reference }, callSign, token, onChange, t } = this.props;

      try {
        await this.waitConfirmation(t('proposition:cancelTheProposition'), 'CANCEL');
        try {
          const cancelled = await SalesPropositionApi.cancelSalesProposition(callSign, token, reference);
          onChange(cancelled);

          actions.openSnackbar('message', t('propositionCancelled'));
        } catch (error) {
          actions.openSnackbar('error', t('propositionCancelledError'));
        }
      } catch (error) {
        // Do nothing if cancelled
      }
    }

    async onDeclineProposition() {
      const { actions, proposition: { reference }, callSign, token, onChange, t } = this.props;

      try {
        await this.waitConfirmation(t('proposition:declineTheProposition'), 'DECLINE');
        try {
          const declined = await SalesPropositionApi.declineSalesProposition(callSign, token, reference);
          onChange(declined);

          actions.openSnackbar('message', t('propositionDeclined'));
        } catch (error) {
          actions.openSnackbar('error', t('propositionDeclinedError'));
        }
      } catch (error) {
        // Do nothing if cancelled
      }
    }

    async onAcceptSalesProposition() {
      const { actions, proposition: { reference }, callSign, token, onChange, t } = this.props;

      try {
        await this.waitConfirmation(t('proposition:acceptTheProposition'), 'ACCEPT');
        try {
          const accepted = await SalesPropositionApi.accept(callSign, token, reference);
          onChange(accepted);

          actions.openSnackbar('message', t('propositionAccepted'));
        } catch (error) {
          actions.openSnackbar('error', t('propositionAcceptedError'));
        }
      } catch (error) {
        // Do nothing if cancelled
      }
    }

    async onModifySalesProposition() {
      const { actions, proposition: { reference }, callSign, token } = this.props;

      try {
        const propositionToModify = await SalesPropositionApi.fetchSalesProposition(callSign, token, reference);
        this.setState({ propositionToModify, sending: false });
      } catch (error) {
        actions.openSnackbar('error', 'could not fetch sales proposition');
      }
    }

    onProposeSalesProposition = async (newProposition: Proposition) => {
      const { propositionToModify, sending } = this.state;
      if (!propositionToModify) {
        return;
      }

      const { actions, t, callSign, token, onChange } = this.props;

      if (!sending) {
        try {
          const modified = await SalesPropositionApi.modify(callSign, token, propositionToModify, newProposition);
          onChange(modified);

          actions.openSnackbar('message', t('propositionModified'));
        } catch (error) {
          actions.openSnackbar('error', t('propositionModifiedError'));
        }
      } else {
        try {
          const modified = await SalesPropositionApi.modify(callSign, token, propositionToModify, newProposition);
          await this.sendSalesProposition(modified);
        } catch (error) {
          actions.openSnackbar('error', t('sendPropositionError'));
        }
      }
    }

    async onSendSalesProposition() {
      const { callSign, token, proposition: { reference } } = this.props;
      const toSend = await SalesPropositionApi.fetchSalesProposition(callSign, token, reference);

      if (toSend.hasRequiredInformations()) {
        await this.sendSalesProposition(toSend);
      } else {
        this.setState({ propositionToModify: toSend, sending: true });
      }
    }

    async sendSalesProposition(proposition: Proposition) {
      const { actions, callSign, token, onChange, t } = this.props;

      try {
        await this.waitConfirmation(t('proposition:sendTheProposition'), 'SEND');
        try {
          const sent = await SalesPropositionApi.send(callSign, token, proposition.reference);
          onChange(sent);

          actions.openSnackbar('message', t('propositionSent'));
        } catch (error) {
          actions.openSnackbar('error', t('sendPropositionError'));
        }
      } catch (error) {
        // Do nothing if cancelled
      }
    }

    getActionsOf(): Action[] {
      const { t, proposition } = this.props;
      const actions = [];

      if (proposition.isProposed()) {
        actions.push(
          { label: t('acceptForCustomer'), type: 'ACCEPT', onAction: () => this.onAcceptSalesProposition() },
          { label: t('declineForCustomer'), type: 'DECLINE', onAction: () => this.onDeclineProposition() },
        );
      }

      if (proposition.isDrafted()) {
        actions.push(
          { label: t('send'), type: 'SEND', onAction: () => this.onSendSalesProposition() },
          { label: t('cancel'), type: 'CANCEL', onAction: () => this.onCancelProposition() },
        );
      }

      if (!proposition.isAccepted() && !proposition.isCancelled()) {
        actions.push(
          { label: t('modify'), type: 'MODIFY', onAction: () => this.onModifySalesProposition() },
        );
      }

      return actions;
    }

    render() {
      const { propositionToModify, sending } = this.state;
      const passThroughProps = _.omit(this.props, 'error');

      return (
        <React.Fragment>
          <WrappedComponent {...passThroughProps} actions={this.getActionsOf()} />
          {propositionToModify &&
          <SavePropositionDialog
            proposition={propositionToModify}
            shouldHaveAllInformations={Boolean(sending)}
            onClose={this.clearEditingState}
            onPropose={this.onProposeSalesProposition}
          />
          }
          <ActionOnPropositionConfirmationDialog
            {...this.state.confirmation}
            proposition={this.props.proposition}
          />
        </React.Fragment>
      );
    }
  }
  WithPropositionActions.displayName = `WithPropositionActions(${getDisplayName(WrappedComponent)})`;
  return WithPropositionActions;
}

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

function mapStateToProps(state) {
  return {
    user: getUser(state),
    callSign: state.session.radio.callSign,
    token: state.session.token,
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({ openSnackbar }, dispatch),
  };
}

export default compose(translate('sales', 'proposition'), connect(mapStateToProps, mapDispatchToProps), withPropositionActions);



// WEBPACK FOOTER //
// ./src/statsradio/modules/proposition/decorators/withPropositionActions.js